import {config} from 'config';
import {autobind} from 'core-decorators';
import {inject, injectable} from 'inversify';
import {action, computed, observable} from 'mobx';
import {CardModel, CardType, EvaluationMark, PreloadBufferItem, TopicModel} from '../interfaces/models/CardModel';
import Logger from '../utils/Logger';
import {CourseStore} from './CourseStore';
import {UserStore} from './UserStore';
import {CardSourceSymbol, ICardSourceAdapter} from "./adapters/ICardSourceAdapter";
import {IProgressSourceAdapter, ProgressSourceSymbol} from "./adapters/IProgressSourceAdapter";
import {ILocalStorageAdapter, LocalStorageSymbol} from "./adapters/ILocalStorageAdapter";
import {ISentryAdapter, SentrySymbol} from "./adapters/ISentryAdapter";

@injectable()
export class CardStore {

    loadingBuffer;

    @observable currentCard: CardModel;
    @observable isSkipTopicAlert = false;
    @observable _isLoading = false;

    @observable showLegAchievement = false;
    @observable showMilestoneAchievement = false;

    @observable isLegAchievementActive = false;
    @observable isMilestoneAchievementActive = false;

    @observable isBufferLoaded = false;

    @observable displayBackButton = false;
    @observable isChangeMode = false;
    previousBuffer;
    previousActiveCard;

    playLegAudioCallback: () => any;

    @observable progress = {
        achievement: null,
        days: null,
        current: 1,
        total: 15,
    };

    @observable nextBuffer: PreloadBufferItem[] = [];

    set isLoading(value) {
        clearTimeout(this.loadingBuffer);

        if (value) {
            this.loadingBuffer = setTimeout(() => {
                this._isLoading = true;
            }, 650);
        } else {
            this._isLoading = false;
        }
    }

    @computed
    get isLoading() {
        return this._isLoading;
    }

    constructor(
        // private cardApi: CardApi,
        // private progressApi: ProgressApi,
        @inject(SentrySymbol) private sentryAdapter: ISentryAdapter,
        @inject(CardSourceSymbol) private cardSource: ICardSourceAdapter,
        @inject(ProgressSourceSymbol) private progressSource: IProgressSourceAdapter,
        private courseStore: CourseStore,
        private userStore: UserStore,
    ) {}

    async getTopic(topicId): Promise<TopicModel> {
        return this.cardSource.getTopic(this.courseStore.currentCourse.id, topicId);
    }

    async loadCardPreview(cardId: number) {
        this.currentCard = (await this.cardSource.getCardById(cardId));
    }

    /**
     * Load the next card to store
     */
    async loadCard(loadNextCard = false, skipAchievements = false) {
        try {
            this.isBufferLoaded = false;

            if (!this.currentCard || loadNextCard) {
                this.currentCard = (await this.cardSource.getCard(this.courseStore.currentCourse.id));
                await this.loadBuffer();

                this.isBufferLoaded = true;
            } else {
                this.currentCard = this.getNextCard(this.nextBuffer[ 0 ]);
            }

            if (!skipAchievements) {
                this.processAchievements();
            }
        } catch (e) {
            Logger.error(e);
        } finally {
            this.isLoading = false;
        }
    }

    async loadBuffer() {
        const bufferCard = await this.preloadCard();
        // this.nextBuffer[0] = bufferCard.data;
        this.nextBuffer[0] = bufferCard;

        const previousCard = this.nextBuffer[0].previousCard;

        if (previousCard) {
            this.displayBackButton = true;
        } else {
            this.displayBackButton = false;
        }

        this.isBufferLoaded = true;

        if (this.progress.days > 0 || !this.progress.days) {
            await this.increaseProgress();
        }
    }

    getNextCard(preLoadedCard: PreloadBufferItem) {
        return preLoadedCard.nextCard;
    }

    async preloadCard() {
        // return this.cardApi.getPreload(this.courseStore.currentCourse.id);
        const result = await this.cardSource.preload(this.courseStore.currentCourse.id);

        if (this.currentCard && result.nextCard && result.nextCard.id && result.nextCard.id === this.currentCard?.id) {
            this.sentryAdapter.captureMessage("SameCards", {
                scope: scope => {
                    scope.setExtra('current', this.currentCard);
                    scope.setExtra('preload', result);
                    scope.setUser({ id: this.userStore.user?.id });
                }
            })
        }

        return result;
    }

    /**
     * Back to the previous card
     */
    @action.bound async back() {
        if (!this.isBufferLoaded) {
            return;
        }

        this.displayBackButton = false;
        this.isChangeMode = true;

        this.previousBuffer = JSON.parse(JSON.stringify(this.nextBuffer));
        this.previousActiveCard = JSON.parse(JSON.stringify(this.currentCard));

        const updatedBuffer: PreloadBufferItem = {
            previousCard: null,
            nextCard: {
                ...this.currentCard
            }
        };

        this.currentCard = this.nextBuffer[0].previousCard;
        this.nextBuffer[0] = updatedBuffer;
    }

    /**
     * Skip the card and load the next one
     */
    @action.bound async skip({ skipTopic } = { skipTopic: false }) {
        if (!this.isBufferLoaded) {
            return;
        }

        const skipped9 = this.nextBuffer[0] &&
            this.nextBuffer[0].skippedPosition &&
            this.nextBuffer[0].skippedPosition === 9 ;

        if (this.isChangeMode) {
            this.currentCard = this.previousActiveCard; // @todo check if it's ok
            await this._disableChangingMode();

            return;
        }

        if (skipTopic) {
            this.userStore.user.topic_group_skipped = true;
        }

        this.isLoading = true;

        try {
            const cardToSkip = this.currentCard?.id;
            const isGrammar = this.currentCard.card_type === CardType.EXPLANATION || this.currentCard.card_type === CardType.EXERCISE;
            const isGrammarAlreadySkipped = this.userStore.user && this.userStore.user.topic_group_skipped;

            if (isGrammar && !skipTopic && !this.isSkipTopicAlert && !isGrammarAlreadySkipped) {
                this.isSkipTopicAlert = true;
                return;
            }

            if (!isGrammar && !skipped9) {
                await this.loadCard(false, true);
            }

            // await this.cardApi.skip(cardToSkip, this.currentCard.id);
            await this.cardSource.skip(this.courseStore.currentCourse.id, cardToSkip, this.currentCard?.id);

            if (isGrammar || skipped9) {
                await this.loadCard(true);
            } else {
                await this.loadBuffer();
            }
        } catch (e) {
            Logger.error(e);
        } finally {
            this.isLoading = false;
        }
    }

    @action async _disableChangingMode(forced = false) {
        if (this.isChangeMode || forced) {
            // await this.loadCard();
            this.isChangeMode = false;

            this.nextBuffer = [ ...this.previousBuffer ];

            // this.nextBuffer = null;
            this.isBufferLoaded = true;
            this.displayBackButton = true;
        }
    }

    /**
     * Answer grammar card
     */
    @action.bound async answerGrammarCard(log: string, mark: EvaluationMark) {
        if (!this.isBufferLoaded) {
            return false;
        }

        this.isLoading = true;

        try {
            const cardId = this.currentCard.id;

            await this.loadCard();
            // await this.cardApi.evaluate(cardId, this.currentCard.id, mark, false, log);
            await this.cardSource.evaluate(cardId, this.courseStore.currentCourse.id, this.currentCard.id, mark, false, log);

            return this.loadBuffer();
        } catch (e) {
            Logger.error(e);
        } finally {
            this.isLoading = false;
        }
    }

    @action.bound async increaseProgress() {
        // this.progress.current += 1;
        // this.progressApi =
        // const response = await this.progressApi.getProgressBar(this.courseStore.currentCourse.id);
        const response = await this.progressSource.getProgressBar(this.courseStore.currentCourse.id);
        // const { answers, answers_days, answers_today } = response.data;
        const { answers, answers_days, answers_today } = response;

        this.progress.days = answers_days;
        this.progress.total = (answers / answers_days);
        this.progress.current = answers_today;

        if (response.achievement) {
            this.progress.achievement = response.achievement;
        }
    }

    @autobind processAchievements() {
        if (this.currentCard && this.currentCard.word_rank === this.userStore.progress.next_position) {
            if (this.playLegAudioCallback) {
                this.playLegAudioCallback();
            }

            this.showLegAchievement = true;
            this.isLegAchievementActive = true;
        }

        /*if (this.currentCard && this.currentCard.word_rank === this.userStore.progress.milestone.level * config.milestoneCards) {
            this.showMilestoneAchievement = true;
            this.isMilestoneAchievementActive = true;
        }*/
    }

    reset() {
        this.loadingBuffer = false;
        this.currentCard = null;
        this.isSkipTopicAlert = false;
        this._isLoading = false;
        this.showLegAchievement = false;
        this.showMilestoneAchievement = false;
        this.isLegAchievementActive = false;
        this.isMilestoneAchievementActive = false;
        this.isBufferLoaded = false;
        this.displayBackButton = false;
        this.isChangeMode = false;
        this.previousBuffer = null;
        this.progress = {
            achievement: null,
            days: null,
            current: 1,
            total: 15,
        };
        this.nextBuffer = [];
    }

}
